import { apiClient } from './api';
import type {
  Subcontractor,
  SubcontractorFormData,
  SubcontractorListResponse,
  SubcontractorSpecialty,
  SubcontractorFilters,
  SubcontractorStatistics,
  WorkOrder,
  WorkOrderFormData,
  WorkOrderListResponse,
  WorkOrderFilters,
  SubcontractorInvoice,
  SubcontractorInvoiceFormData,
  SubcontractorInvoiceListResponse,
  SubcontractorInvoiceFilters,
} from '../types/subcontractors';

// ============================================================================
// Subcontractors API
// ============================================================================

export const subcontractorsApi = {
  // List all subcontractors with optional filters
  list: async (filters?: SubcontractorFilters, page = 1): Promise<SubcontractorListResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach(v => params.append(key, v.toString()));
          } else {
            params.append(key, value.toString());
          }
        }
      });
    }
    params.append('page', page.toString());
    
    const response = await apiClient.get(`/api/subcontractors/?${params.toString()}`);
    return response.data as SubcontractorListResponse;
  },

  // Get a single subcontractor by ID
  get: async (id: number): Promise<Subcontractor> => {
    const response = await apiClient.get(`/api/subcontractors/${id}/`);
    return response.data as Subcontractor;
  },

  // Create a new subcontractor
  create: async (data: SubcontractorFormData): Promise<Subcontractor> => {
    const response = await apiClient.post('/api/subcontractors/', data);
    return response.data as Subcontractor;
  },

  // Update an existing subcontractor
  update: async (id: number, data: Partial<SubcontractorFormData>): Promise<Subcontractor> => {
    const response = await apiClient.patch(`/api/subcontractors/${id}/`, data);
    return response.data as Subcontractor;
  },

  // Delete a subcontractor
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/subcontractors/${id}/`);
  },

  // Archive multiple subcontractors
  bulkArchive: async (ids: number[]): Promise<{ message: string; count: number }> => {
    const response = await apiClient.post('/api/subcontractors/bulk-archive/', { ids });
    return response.data as { message: string; count: number };
  },

  // Get work orders for a subcontractor
  getWorkOrders: async (id: number, status?: string): Promise<WorkOrder[]> => {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get(`/api/subcontractors/${id}/work-orders/${params}`);
    return response.data as WorkOrder[];
  },

  // Get invoices for a subcontractor
  getInvoices: async (id: number, status?: string): Promise<SubcontractorInvoice[]> => {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get(`/api/subcontractors/${id}/invoices/${params}`);
    return response.data as SubcontractorInvoice[];
  },

  // Get statistics for a subcontractor
  getStatistics: async (id: number): Promise<SubcontractorStatistics> => {
    const response = await apiClient.get(`/api/subcontractors/${id}/statistics/`);
    return response.data as SubcontractorStatistics;
  },
};

// ============================================================================
// Subcontractor Specialties API
// ============================================================================

export const specialtiesApi = {
  // List all specialties
  list: async (): Promise<SubcontractorSpecialty[]> => {
    const response = await apiClient.get('/api/subcontractor-specialties/');
    return (response.data as any).results || [];
  },

  // Get a single specialty
  get: async (id: number): Promise<SubcontractorSpecialty> => {
    const response = await apiClient.get(`/api/subcontractor-specialties/${id}/`);
    return response.data as SubcontractorSpecialty;
  },

  // Create a new specialty
  create: async (data: { name: string; description?: string }): Promise<SubcontractorSpecialty> => {
    const response = await apiClient.post('/api/subcontractor-specialties/', data);
    return response.data as SubcontractorSpecialty;
  },

  // Update a specialty
  update: async (id: number, data: Partial<{ name: string; description?: string; is_active: boolean }>): Promise<SubcontractorSpecialty> => {
    const response = await apiClient.patch(`/api/subcontractor-specialties/${id}/`, data);
    return response.data as SubcontractorSpecialty;
  },

  // Delete a specialty
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/subcontractor-specialties/${id}/`);
  },
};

// ============================================================================
// Work Orders API
// ============================================================================

export const workOrdersApi = {
  // List all work orders with optional filters
  list: async (filters?: WorkOrderFilters, page = 1): Promise<WorkOrderListResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    params.append('page', page.toString());
    
    const response = await apiClient.get(`/api/subcontractor-work-orders/?${params.toString()}`);
    return response.data as WorkOrderListResponse;
  },

  // Get a single work order
  get: async (id: number): Promise<WorkOrder> => {
    const response = await apiClient.get(`/api/subcontractor-work-orders/${id}/`);
    return response.data as WorkOrder;
  },

  // Create a new work order
  create: async (data: WorkOrderFormData): Promise<WorkOrder> => {
    const response = await apiClient.post('/api/subcontractor-work-orders/', data);
    return response.data as WorkOrder;
  },

  // Update a work order
  update: async (id: number, data: Partial<WorkOrderFormData>): Promise<WorkOrder> => {
    const response = await apiClient.patch(`/api/subcontractor-work-orders/${id}/`, data);
    return response.data as WorkOrder;
  },

  // Delete a work order
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/subcontractor-work-orders/${id}/`);
  },

  // Send work order to subcontractor
  send: async (id: number): Promise<WorkOrder> => {
    const record = await workOrdersApi.get(id);
    const response = await apiClient.post(`/api/subcontractor-work-orders/${id}/send/`, { request_id: crypto.randomUUID(), expected_version: record.version });
    return response.data as WorkOrder;
  },

  // Accept work order
  accept: async (id: number): Promise<WorkOrder> => {
    const response = await apiClient.post(`/api/subcontractor-work-orders/${id}/accept/`, {});
    return response.data as WorkOrder;
  },

  // Reject work order
  reject: async (id: number): Promise<WorkOrder> => {
    const response = await apiClient.post(`/api/subcontractor-work-orders/${id}/reject/`, {});
    return response.data as WorkOrder;
  },

  // Start work on work order
  start: async (id: number): Promise<WorkOrder> => {
    const response = await apiClient.post(`/api/subcontractor-work-orders/${id}/start/`, {});
    return response.data as WorkOrder;
  },

  // Complete work order
  complete: async (id: number, data: { notes?: string; quality_rating?: number }): Promise<WorkOrder> => {
    const response = await apiClient.post(`/api/subcontractor-work-orders/${id}/complete/`, data);
    return response.data as WorkOrder;
  },

  // Cancel work order
  cancel: async (id: number): Promise<WorkOrder> => {
    const response = await apiClient.post(`/api/subcontractor-work-orders/${id}/cancel/`, {});
    return response.data as WorkOrder;
  },

  // Get scheduled work orders for calendar
  getScheduled: async (startDate?: string, endDate?: string): Promise<WorkOrder[]> => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const response = await apiClient.get(`/api/subcontractor-work-orders/scheduled/?${params.toString()}`);
    return response.data as WorkOrder[];
  },
};

// ============================================================================
// Subcontractor Invoices API
// ============================================================================

export const subcontractorInvoicesApi = {
  // List all invoices with optional filters
  list: async (filters?: SubcontractorInvoiceFilters, page = 1): Promise<SubcontractorInvoiceListResponse> => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    params.append('page', page.toString());
    
    const response = await apiClient.get(`/api/subcontractor-invoices/?${params.toString()}`);
    return response.data as SubcontractorInvoiceListResponse;
  },

  // Get a single invoice
  get: async (id: number): Promise<SubcontractorInvoice> => {
    const response = await apiClient.get(`/api/subcontractor-invoices/${id}/`);
    return response.data as SubcontractorInvoice;
  },

  // Create a new invoice
  create: async (data: SubcontractorInvoiceFormData): Promise<SubcontractorInvoice> => {
    const response = await apiClient.post('/api/subcontractor-invoices/', data);
    return response.data as SubcontractorInvoice;
  },

  // Update an invoice
  update: async (id: number, data: Partial<SubcontractorInvoiceFormData>): Promise<SubcontractorInvoice> => {
    const response = await apiClient.patch(`/api/subcontractor-invoices/${id}/`, data);
    return response.data as SubcontractorInvoice;
  },

  // Delete an invoice
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/subcontractor-invoices/${id}/`);
  },

  // Approve or reject an invoice
  approveReject: async (id: number, action: 'approve' | 'reject', notes?: string): Promise<SubcontractorInvoice> => {
    const response = await apiClient.post(`/api/subcontractor-invoices/${id}/approve-reject/`, {
      action,
      notes,
    });
    return response.data as SubcontractorInvoice;
  },

  // Record payment for an invoice
  recordPayment: async (
    id: number,
    data: {
      payment_type: 'full' | 'partial';
      amount?: number;
      payment_method: 'check' | 'ach' | 'wire' | 'credit_card' | 'cash';
      payment_date?: string;
      reference?: string;
      check_number?: string;
    }
  ): Promise<SubcontractorInvoice> => {
    const response = await apiClient.post(`/api/subcontractor-invoices/${id}/record-payment/`, data);
    return response.data as SubcontractorInvoice;
  },

  // Dispute an invoice
  dispute: async (id: number, reason: string): Promise<SubcontractorInvoice> => {
    const response = await apiClient.post(`/api/subcontractor-invoices/${id}/dispute/`, { reason });
    return response.data as SubcontractorInvoice;
  },

  // Resolve a disputed invoice
  resolveDispute: async (id: number, resolution: string): Promise<SubcontractorInvoice> => {
    const response = await apiClient.post(`/api/subcontractor-invoices/${id}/resolve-dispute/`, { resolution });
    return response.data as SubcontractorInvoice;
  },

  // Get pending approval invoices
  getPendingApproval: async (): Promise<SubcontractorInvoice[]> => {
    const response = await apiClient.get('/api/subcontractor-invoices/pending-approval/');
    return response.data as SubcontractorInvoice[];
  },

  // Get overdue invoices
  getOverdue: async (): Promise<SubcontractorInvoice[]> => {
    const response = await apiClient.get('/api/subcontractor-invoices/overdue/');
    return response.data as SubcontractorInvoice[];
  },
};

// ============================================================================
// File Upload Helper
// ============================================================================

export const uploadSubcontractorDocument = async (
  subcontractorId: number,
  file: File,
  documentType: 'w9' | 'insurance' | 'other'
): Promise<{ url: string }> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('document_type', documentType);

  const response = await apiClient.post(
    `/api/subcontractors/${subcontractorId}/upload-document/`,
    formData
  );

  return response.data as { url: string };
};